import type {
  AgentUsageDoor,
  AgentUsageInput,
} from '@travel-planner/shared/schemas/agent-usage';
import { UTCDate } from '@date-fns/utc';
import {
  AGENT_USAGE_DOORS,
  AGENT_USAGE_MAX_DAYS,
} from '@travel-planner/shared/schemas/agent-usage';
import { addDays, differenceInDays, format } from 'date-fns';

/** Days in the default period, today included. */
export const AI_USAGE_DEFAULT_DAYS = 30;

export type AiUsageSearch = {
  from?: string;
  to?: string;
  doors?: AgentUsageDoor[];
  /** `1`: count test sessions too; absent means real use only. */
  tests?: 1;
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;

const asDay = (v: unknown): string | undefined =>
  typeof v === 'string' &&
  DAY.test(v) &&
  !Number.isNaN(Date.parse(`${v}T00:00:00Z`))
    ? v
    : undefined;

const isDoor = (v: unknown): v is AgentUsageDoor =>
  (AGENT_USAGE_DOORS as readonly unknown[]).includes(v);

/** Every door (or none) means no door filter. */
export const normaliseDoors = (
  values: readonly unknown[]
): AgentUsageDoor[] | undefined => {
  const doors = AGENT_USAGE_DOORS.filter((door) => values.includes(door));
  return doors.length === 0 || doors.length === AGENT_USAGE_DOORS.length
    ? undefined
    : doors;
};

const asDoors = (v: unknown): AgentUsageDoor[] | undefined => {
  const raw = Array.isArray(v) ? v : typeof v === 'string' ? v.split(',') : [];
  return normaliseDoors(raw.filter(isDoor));
};

export const validateAiUsageSearch = (
  input: Record<string, unknown>
): AiUsageSearch => ({
  from: asDay(input.from),
  to: asDay(input.to),
  doors: asDoors(input.doors),
  tests:
    input.tests === 1 || input.tests === '1' || input.tests === true
      ? 1
      : undefined,
});

const toUtcDay = (day: string): UTCDate => new UTCDate(`${day}T00:00:00Z`);

export const shiftDay = (day: string, days: number): string =>
  format(addDays(toUtcDay(day), days), 'yyyy-MM-dd');

export const todayUtc = (now: UTCDate = new UTCDate()): string =>
  format(now, 'yyyy-MM-dd');

export const defaultPeriod = (today: string) => ({
  from: shiftDay(today, -(AI_USAGE_DEFAULT_DAYS - 1)),
  to: today,
});

/** The report input for the URL's filters: defaults fill gaps, the period stays within a year. */
export const toReportInput = (
  search: AiUsageSearch,
  today: string
): AgentUsageInput => {
  const fallback = defaultPeriod(today);
  let to = search.to ?? fallback.to;
  let from = search.from ?? shiftDay(to, -(AI_USAGE_DEFAULT_DAYS - 1));
  if (from > to) {
    from = fallback.from;
    to = fallback.to;
  }
  if (differenceInDays(toUtcDay(to), toUtcDay(from)) >= AGENT_USAGE_MAX_DAYS) {
    from = shiftDay(to, -(AGENT_USAGE_MAX_DAYS - 1));
  }
  return {
    from,
    to,
    ...(search.doors ? { doors: search.doors } : {}),
    ...(search.tests === 1 ? { includeTests: true } : {}),
  };
};
